import { memo, useLayoutEffect, useRef, useState } from 'react';
import type { PlateDef } from '../data/plates';
import { PlateMini } from './PlateMini';

interface Props {
  plate: PlateDef;
  claimed: boolean;
  onClaim: (plateId: string) => void;
  onUndo: (plateId: string) => void;
}

/**
 * One row of the plate list. Deliberately a big single tap target rather than a
 * grid tile: the whole row is pressable, which is what you want when the car is
 * moving and you are not looking closely at the screen.
 *
 * The row is a toggle: tapping a claimed plate takes it back off. That is a
 * single tap on the same target that claimed it, with no confirm, which is only
 * reasonable because it is not destructive - tapping again re-claims the plate.
 * The one thing a mis-tap really loses is the original claim's timestamp.
 *
 * The "look for" description is clamped to two lines, which on a narrow phone
 * often ellipsises useful detail. When that happens a chevron appears so it
 * can be expanded in place. The row can no longer be a real <button> once it
 * hosts that second, independent tap target - a <button> may not contain
 * another interactive element - so it is a div with role="button" instead,
 * given the same click and keyboard behaviour a real button would have.
 */
function PlateRowInner({ plate, claimed, onClaim, onUndo }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const lookRef = useRef<HTMLSpanElement>(null);

  // Detect whether the clamp is actually cutting text off, so the chevron only
  // shows up when there is something more to reveal. Re-checked on resize -
  // rotating the phone changes how many words fit on a line.
  useLayoutEffect(() => {
    const el = lookRef.current;
    if (!el || expanded) return;
    const measure = () => setCanExpand(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [plate.lookFor, expanded]);

  const toggleClaim = () => (claimed ? onUndo(plate.id) : onClaim(plate.id));

  return (
    <div
      className={`plate${claimed ? ' claimed' : ''}`}
      role="button"
      tabIndex={0}
      onClick={toggleClaim}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleClaim();
        }
      }}
      aria-pressed={claimed}
      aria-label={
        claimed
          ? `${plate.name}, claimed. Tap to remove.`
          : `Claim ${plate.name}, worth ${plate.points} points`
      }
    >
      <span className="plate-swatch" aria-hidden="true">
        <PlateMini plate={plate} />
      </span>

      <span className="plate-body">
        <span className="plate-name">{plate.name}</span>
        <span className="plate-look-row">
          <span ref={lookRef} className={`plate-look${expanded ? ' expanded' : ''}`}>
            {plate.lookFor}
          </span>
          {(canExpand || expanded) && (
            <button
              type="button"
              className={`plate-expand${expanded ? ' expanded' : ''}`}
              // Its own tap target nested in the row's - stop the click (and
              // the Enter/Space keydown that triggers it) from also bubbling
              // up as a claim/undo tap on the row underneath.
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((value) => !value);
              }}
              onKeyDown={(event) => event.stopPropagation()}
              aria-expanded={expanded}
              aria-label={expanded ? 'Show less' : 'Show more'}
            >
              ▾
            </button>
          )}
        </span>
      </span>

      <span className="plate-right">
        <span className="plate-points">
          {claimed ? <span className="plate-tick">✓</span> : plate.points.toLocaleString()}
        </span>
        <span className={`rarity ${plate.rarity}`}>{plate.rarity}</span>
      </span>
    </div>
  );
}

// The list renders ~120 rows and re-renders on every claim, so memoising the
// rows keeps a tap feeling instant on an older phone.
export const PlateRow = memo(PlateRowInner);
